import { z } from "zod";
import { AuthSessionSchema } from "../entities";

/**
 * `/api/auth/*` — single-user email-code auth (§10). Request endpoints always
 * respond 200 (`OkResponse`) to avoid account enumeration.
 */

/** `POST /api/auth/claim/request` and `/login/request`. */
export const AuthRequestCodeSchema = z.object({
  email: z.email(),
});
export type AuthRequestCode = z.infer<typeof AuthRequestCodeSchema>;

/** `POST /api/auth/claim/verify` and `/login/verify`. */
export const AuthVerifyCodeSchema = z.object({
  email: z.email(),
  code: z.string().min(4).max(12),
});
export type AuthVerifyCode = z.infer<typeof AuthVerifyCodeSchema>;

/** Response of a successful verify / `GET /api/auth/session`. */
export const AuthSessionResponseSchema = AuthSessionSchema;
export type AuthSessionResponse = z.infer<typeof AuthSessionResponseSchema>;

/**
 * `POST /api/auth/qr/issue` (session-guarded) — mint a short-lived, single-use
 * QR sign-in token for the mobile app. `deeplink` is the exact string to encode
 * in the QR (`richrich://qr-login?token=…`).
 */
export const AuthQrIssueResponseSchema = z.object({
  token: z.string(),
  expiresAt: z.number(),
  deeplink: z.string(),
});
export type AuthQrIssueResponse = z.infer<typeof AuthQrIssueResponseSchema>;

/** `POST /api/auth/qr/verify` — exchange a scanned QR token for a session. */
export const AuthQrVerifySchema = z.object({
  token: z.string().min(16).max(256),
});
export type AuthQrVerify = z.infer<typeof AuthQrVerifySchema>;

/**
 * Client-class hint (§10). The mobile app sends `X-Richrich-Client: mobile` on
 * every request; the web SPA never sends it. The server uses the class only to
 * pick the session's sliding lifetime (web 7 days, mobile 365 days) — it grants
 * nothing on its own, since a session still requires a valid cookie. A browser
 * page on another origin cannot attach a custom header without a CORS preflight,
 * and the Worker answers no preflight, so it cannot claim the mobile class.
 */
export const CLIENT_CLASS_HEADER = "X-Richrich-Client";

/** The {@link CLIENT_CLASS_HEADER} value the mobile app sends. */
export const MOBILE_CLIENT_CLASS = "mobile";

/** Which lifetime class a session belongs to. Anything not `mobile` is `web`. */
export type ClientClass = "web" | "mobile";

/** Map a raw {@link CLIENT_CLASS_HEADER} value to a {@link ClientClass}. */
export function parseClientClass(value: string | null | undefined): ClientClass {
  return value?.trim().toLowerCase() === MOBILE_CLIENT_CLASS ? "mobile" : "web";
}
